import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Search } from "lucide-react";
import { productAPI, fpirAPI } from "../../services/api";

// Client-side FPIR list — read-only. Backend already filters to status='Final'
// only for client_user, so no Draft reports can ever reach this page.
export default function ClientFpirTemplate() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [search, setSearch] = useState("");
  const [date, setDate] = useState("");
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    productAPI.list().then((r) => setProducts(r.data || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit: pageSize, offset: (page - 1) * pageSize };
    if (productId) params.product_id = productId;
    if (search.trim()) params.search = search.trim();
    if (date) params.date = date;
    fpirAPI
      .listReports(params)
      .then((r) => {
        setReports(r.data.reports || []);
        setTotal(r.data.total || 0);
      })
      .finally(() => setLoading(false));
  }, [productId, search, date, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [productId, search, date]);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>FPIR Reports</h1>
        <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
          First / Inprocess / Last Piece Inspection Reports
        </p>
      </div>

      {/* Filters */}
      <div
        className="card"
        style={{
          marginBottom: 20,
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "flex-end",
        }}
      >
        <div
          className="form-group"
          style={{ minWidth: 240, flex: "1 1 240px" }}
        >
          <label>Search (Part ID / Name)</label>
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text3)",
              }}
            />
            <input
              type="text"
              placeholder="e.g. P001 or Part1"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 30 }}
            />
          </div>
        </div>
        <div className="form-group" style={{ minWidth: 200 }}>
          <label>Part</label>
          <select
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
          >
            <option value="">All Products</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        {(search || date || productId) && (
          <button
            className="btn-secondary"
            onClick={() => {
              setSearch("");
              setDate("");
              setProductId("");
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <div
            style={{ padding: 24, textAlign: "center", color: "var(--text2)" }}
          >
            Loading...
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Part</th>
                <th>Shift</th>
                <th>Checked By</th>
                <th>Approved By</th>
                <th style={{ width: 70 }}>View</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "var(--text3)",
                    }}
                  >
                    No FPIR reports yet
                  </td>
                </tr>
              )}
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.date).toLocaleDateString()}</td>
                  <td style={{ fontWeight: 500 }}>
                    {r.product_name}{" "}
                    <span className="text3 mono" style={{ fontSize: 11 }}>
                      ({r.product_code})
                    </span>
                  </td>
                  <td className="text2">{r.shift || "—"}</td>
                  <td className="text2">{r.checked_by || "—"}</td>
                  <td className="text2">{r.approved_by || "—"}</td>
                  <td>
                    <button
                      title="View"
                      onClick={() => navigate(`/client/fpir/${r.id}`)}
                      style={iconBtn}
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > pageSize && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 14,
            alignItems: "center",
          }}
        >
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of{" "}
            {total}
          </span>
          <button
            className="btn-secondary"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <button
            className="btn-secondary"
            disabled={page * pageSize >= total}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "var(--surface2)",
  border: "1px solid var(--border2)",
  borderRadius: 6,
  padding: 5,
  cursor: "pointer",
  color: "var(--text2)",
  display: "flex",
};
